import { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertTriangle,
  CheckCircle2,
  Send,
  Loader2,
  RefreshCw,
  ChevronDown,
  Search,
  Filter,
  Download,
  Eye,
  Plus,
  Calendar,
  UserPlus,
  UserMinus,
  ClipboardList,
  Link2,
  FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import CollegaFicDialog from "./CollegaFicDialog";
import ImportClientiFicDialog from "./ImportClientiFicDialog";
import {
  STATO_FATTURA_CONTROLLO,
  STATO_PAGAMENTO_CONTROLLO,
  FILTRO_STATO_CONTROLLO,
  filterRigheControllo,
} from "../utils/fatturazioneControlloMensile";
import { OrigineImportiBadge } from "./FatturazioneKpiRow";
import { ficEiStatusLabel } from "../utils/fatturazioneSdi";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatoBadge({ stato, map }) {
  const cfg = map[stato] || { label: stato, className: "bg-slate-100 text-slate-700" };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        cfg.className,
      )}
      title={stato === "in_coda_sdi" ? "Invio allo SDI in corso in background" : undefined}
    >
      {cfg.spinning && <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" aria-hidden />}
      {cfg.label}
    </span>
  );
}

function AvatarIniziali({ iniziali }) {
  const colors = [
    "bg-emerald-100 text-emerald-800",
    "bg-blue-100 text-blue-800",
    "bg-violet-100 text-violet-800",
    "bg-amber-100 text-amber-800",
  ];
  const idx = (iniziali?.charCodeAt(0) || 0) % colors.length;
  return (
    <span
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold shrink-0",
        colors[idx],
      )}
    >
      {iniziali || "?"}
    </span>
  );
}

function ExtraCell({ extra, dettaglio, formatEuro }) {
  if (!extra || extra <= 0) {
    return <span className="text-slate-400">—</span>;
  }
  const items = dettaglio || [];
  if (!items.length) {
    return <span className="font-medium tabular-nums">{formatEuro(extra)}</span>;
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 font-medium text-emerald-700 hover:underline tabular-nums"
        >
          {formatEuro(extra)}
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="end">
        <p className="text-xs font-semibold text-slate-700 mb-2">Dettaglio extra</p>
        <ul className="space-y-1.5 text-sm">
          {items.map((d) => (
            <li key={d.categoria} className="flex justify-between gap-2">
              <span className="text-slate-600">{d.label}</span>
              <span className="font-medium tabular-nums">{formatEuro(d.importo)}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-between border-t mt-2 pt-2 font-semibold text-sm">
          <span>Totale</span>
          <span>{formatEuro(extra)}</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FicPresenzaCell({ presenza, loading, formatEuro }) {
  if (loading) {
    return <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" />;
  }
  if (!presenza) {
    return <span className="text-slate-400">—</span>;
  }
  if (presenza.tipo === "collegata") {
    const fic = presenza.fic;
    const detail = fic
      ? `N. ${fic.numero ?? "—"} · ${fic.data ?? ""} · ${formatEuro(fic.totale ?? 0)}`
      : "Collegata ad Affidya";
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex">
            <Badge className="text-[10px] bg-sky-100 text-sky-800 cursor-help">Collegata</Badge>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3 text-sm" align="center">
          <p className="font-semibold text-slate-800 mb-1">Fattura collegata su FIC</p>
          {fic && (
            <div className="space-y-0.5 text-slate-600">
              <p>{fic.destinatario || "—"}</p>
              <p className="tabular-nums">{detail}</p>
              {fic.ei_status && (
                <p className="text-xs">SDI: {ficEiStatusLabel(fic.ei_status)}</p>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  }
  if (presenza.su_fic) {
    const fic = presenza.fic || {};
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex">
            <Badge className="text-[10px] bg-emerald-100 text-emerald-800 cursor-help">Sì</Badge>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3 text-sm" align="center">
          <p className="font-semibold text-slate-800 mb-1">Trovata su FIC</p>
          <div className="space-y-0.5 text-slate-600">
            <p>{fic.destinatario || "—"}</p>
            <p className="tabular-nums">
              N. {fic.numero ?? "—"} · {fic.data ?? ""} · {formatEuro(fic.totale ?? 0)}
            </p>
            {fic.ei_status && (
              <p className="text-xs">SDI: {ficEiStatusLabel(fic.ei_status)}</p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200">
      No
    </Badge>
  );
}

export default function FatturazioneControlloMensileTab({
  headers,
  meseControllo,
  setMeseControllo,
  mesiOptions,
  formatMese,
  formatEuro,
  controlloData,
  controlloLoading,
  loadControllo,
  loadFatture,
  fascicoli,
  loadingFascicoli,
  openDetail,
  openRecuperoFic,
  canInviaSdi,
  isFic,
  strutturaIdForConfig,
  registerControlloFullRefresh,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filtroStato, setFiltroStato] = useState("__tutti__");
  const [filtroFascicolo, setFiltroFascicolo] = useState("__tutti__");
  const [soloMancanti, setSoloMancanti] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(null);
  const [ficDialogOpen, setFicDialogOpen] = useState(false);
  const [ficDialogFascicoloId, setFicDialogFascicoloId] = useState(null);
  const [importClientiOpen, setImportClientiOpen] = useState(false);
  const [ficPresenzaByFascicolo, setFicPresenzaByFascicolo] = useState({});
  const [ficPresenzaLoading, setFicPresenzaLoading] = useState(false);
  const [inviaApprovateConfirm, setInviaApprovateConfirm] = useState(null);

  const loadFicPresenza = useCallback(async () => {
    if (!isFic || !meseControllo) {
      setFicPresenzaByFascicolo({});
      return;
    }
    setFicPresenzaLoading(true);
    try {
      const params = { mese: meseControllo };
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      const res = await axios.get(`${API_URL}/api/fatturazione/controllo-mensile/fic-presenza`, {
        headers,
        params,
      });
      const map = {};
      for (const r of res.data?.righe || []) {
        map[r.fascicolo_id] = r;
      }
      setFicPresenzaByFascicolo(map);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || e.response?.data?.message || "Errore verifica presenza su FIC",
      );
      setFicPresenzaByFascicolo({});
    } finally {
      setFicPresenzaLoading(false);
    }
  }, [isFic, meseControllo, headers, strutturaIdForConfig]);

  useEffect(() => {
    loadFicPresenza();
  }, [loadFicPresenza]);

  const refreshControllo = useCallback(async () => {
    await loadControllo();
    await loadFicPresenza();
  }, [loadControllo, loadFicPresenza]);

  useEffect(() => {
    registerControlloFullRefresh?.(refreshControllo);
    return () => registerControlloFullRefresh?.(null);
  }, [refreshControllo, registerControlloFullRefresh]);

  const codaSdiAttiva = Boolean(controlloData?.kpi?.coda_sdi_attiva);

  useEffect(() => {
    if (!codaSdiAttiva) return undefined;
    const id = setInterval(() => {
      loadControllo?.({ silent: true });
      loadFatture?.({ silent: true });
    }, 5000);
    return () => clearInterval(id);
  }, [codaSdiAttiva, loadControllo, loadFatture]);

  const kpi = controlloData?.kpi || {};
  const progresso = controlloData?.progresso || {};
  const righe = useMemo(() => controlloData?.righe || [], [controlloData?.righe]);

  const fascicoliAttivi = useMemo(
    () => (fascicoli || []).filter((f) => f.archiviata !== true),
    [fascicoli],
  );

  const righeFiltrate = useMemo(
    () =>
      filterRigheControllo(righe, {
        filtroStato,
        filtroFascicolo,
        searchQuery,
        soloMancanti,
      }),
    [righe, filtroStato, filtroFascicolo, searchQuery, soloMancanti],
  );

  const isRigaSelezionabile = useCallback(
    (row) => Boolean(row?.fattura_id && row.stato_fattura === "bozza"),
    [],
  );

  const selectableRighe = useMemo(
    () => righeFiltrate.filter(isRigaSelezionabile),
    [righeFiltrate, isRigaSelezionabile],
  );

  const allSelectableSelected =
    selectableRighe.length > 0 &&
    selectableRighe.every((r) => selectedIds.has(r.fattura_id || `f-${r.fascicolo_id}`));

  const someSelectableSelected =
    selectableRighe.some((r) => selectedIds.has(r.fattura_id || `f-${r.fascicolo_id}`)) &&
    !allSelectableSelected;

  const toggleSelect = (fatturaId, fascicoloId, checked) => {
    const key = fatturaId || `f-${fascicoloId}`;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const toggleSelectAll = useCallback(
    (checked) => {
      if (!checked) {
        setSelectedIds(new Set());
        return;
      }
      setSelectedIds(new Set(selectableRighe.map((r) => r.fattura_id || `f-${r.fascicolo_id}`)));
    },
    [selectableRighe],
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.size) return prev;
      const allowed = new Set(selectableRighe.map((r) => r.fattura_id || `f-${r.fascicolo_id}`));
      const next = new Set([...prev].filter((id) => allowed.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectableRighe]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [meseControllo, filtroStato, filtroFascicolo, searchQuery, soloMancanti]);

  const selectedFatturaIds = useMemo(() => {
    const ids = [];
    for (const r of righe) {
      const key = r.fattura_id || `f-${r.fascicolo_id}`;
      if (selectedIds.has(key) && r.fattura_id) ids.push(r.fattura_id);
    }
    return ids;
  }, [righe, selectedIds]);

  const runAction = useCallback(
    async (endpoint, label, { onlySelected = true } = {}) => {
      setActionLoading(endpoint);
      try {
        const body = { mese: meseControllo };
        if (strutturaIdForConfig) body.strutturaId = strutturaIdForConfig;
        if (onlySelected && selectedFatturaIds.length) body.fattura_ids = selectedFatturaIds;
        const res = await axios.post(`${API_URL}/api/fatturazione/controllo-mensile/${endpoint}`, body, {
          headers,
        });
        const isBackgroundInvio = endpoint === "invia-approvate" && res.data?.background;
        if (isBackgroundInvio) {
          toast.success(
            res.data?.message ||
              `${res.data?.accodate ?? 0} fatture accodate per l'invio SDI in background`,
          );
        } else {
          toast.success(res.data?.message || label);
        }
        if (res.data?.errori?.length) {
          res.data.errori.slice(0, 3).forEach((e) => toast.warning(String(e)));
        }
        setSelectedIds(new Set());
        await Promise.all([
          refreshControllo(),
          loadFatture?.({ silent: true }),
        ]);
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || "Operazione non riuscita");
      } finally {
        setActionLoading(null);
      }
    },
    [headers, meseControllo, selectedFatturaIds, refreshControllo, loadFatture, strutturaIdForConfig],
  );

  const openInviaApprovateConfirm = useCallback((mode) => {
    const count =
      mode === "selezionate"
        ? selectedFatturaIds.length
        : kpi.approvate_da_inviare ?? 0;
    setInviaApprovateConfirm({ mode, count });
  }, [selectedFatturaIds.length, kpi.approvate_da_inviare]);

  const confirmInviaApprovate = useCallback(async () => {
    const mode = inviaApprovateConfirm?.mode;
    setInviaApprovateConfirm(null);
    if (mode === "selezionate" && !selectedFatturaIds.length) return;
    await runAction("invia-approvate", "Invio SDI avviato in background", {
      onlySelected: mode === "selezionate",
    });
  }, [inviaApprovateConfirm, selectedFatturaIds.length, runAction]);

  const pctBar =
    progresso.totale > 0 ? Math.round((100 * progresso.fatturati) / progresso.totale) : 0;

  return (
    <div className="space-y-4">
      <Card className="card-base border-slate-200/80">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-2 shrink-0">
              <Calendar className="w-4 h-4 text-slate-500" />
              <Select value={meseControllo} onValueChange={setMeseControllo}>
                <SelectTrigger className="w-[180px] h-9 bg-white font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {mesiOptions.map((m) => (
                    <SelectItem key={m} value={m}>
                      {formatMese(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between text-sm mb-1.5">
                <span className="font-medium text-slate-800">
                  {progresso.fatturati ?? 0} di {progresso.totale ?? 0} ospiti fatturati
                </span>
                <span className="text-slate-500 tabular-nums">{pctBar}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full transition-all"
                  style={{ width: `${pctBar}%` }}
                />
              </div>
              <div className="flex gap-4 mt-1.5 text-xs text-slate-500">
                {(progresso.da_verificare ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-orange-400" />
                    {progresso.da_verificare} da verificare
                  </span>
                )}
                {(progresso.mancanti ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    {progresso.mancanti} mancanti
                  </span>
                )}
                {(kpi.in_coda_sdi ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1 text-sky-700">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {kpi.in_coda_sdi} in coda SDI
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              {isFic && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-sky-200 text-sky-800 hover:bg-sky-50"
                  disabled={controlloLoading}
                  onClick={() => setImportClientiOpen(true)}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Importa anagrafiche FIC
                </Button>
              )}
              {isFic && (progresso.mancanti ?? 0) > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-sky-200 text-sky-800 hover:bg-sky-50"
                  disabled={controlloLoading}
                  onClick={() => {
                    setFicDialogFascicoloId(null);
                    setFicDialogOpen(true);
                  }}
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Collega da FIC
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!!actionLoading || controlloLoading}
                onClick={() => runAction("genera-bozze", "Bozze generate")}
              >
                {actionLoading === "genera-bozze" ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Genera bozze mese
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-violet-600 hover:bg-violet-700 text-white"
                disabled={!!actionLoading || controlloLoading || !selectedFatturaIds.length}
                onClick={() => runAction("approva", "Approvazione completata")}
              >
                {actionLoading === "approva" ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Approva selezionate
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={!!actionLoading || controlloLoading || !canInviaSdi}
                  >
                    {actionLoading === "invia-approvate" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Invia approvate ({kpi.approvate_da_inviare ?? 0})
                    <ChevronDown className="w-4 h-4 ml-1 opacity-80" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={(kpi.approvate_da_inviare ?? 0) === 0}
                    onClick={() => openInviaApprovateConfirm("tutte")}
                  >
                    Invia tutte le approvate del mese
                  </DropdownMenuItem>
                  {selectedFatturaIds.length > 0 && (
                    <DropdownMenuItem onClick={() => openInviaApprovateConfirm("selezionate")}>
                      Invia solo selezionate ({selectedFatturaIds.length})
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-base overflow-hidden">
        <div className="flex flex-col gap-3 p-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <Select value={meseControllo} onValueChange={setMeseControllo}>
              <SelectTrigger className="w-[160px] bg-white h-9 text-sm lg:hidden">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {mesiOptions.map((m) => (
                  <SelectItem key={m} value={m}>
                    {formatMese(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-[160px] bg-white h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTRO_STATO_CONTROLLO.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filtroFascicolo}
              onValueChange={setFiltroFascicolo}
              disabled={loadingFascicoli}
            >
              <SelectTrigger className="w-[180px] bg-white h-9 text-sm">
                <SelectValue placeholder="Tutti gli ospiti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__tutti__">Tutti gli ospiti</SelectItem>
                {fascicoliAttivi.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.nome_completo?.trim() || "Ospite"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9 bg-white h-9"
                placeholder="Cerca ospite…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button type="button" variant="outline" size="sm" className="h-9" onClick={refreshControllo} disabled={controlloLoading}>
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              Aggiorna
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-9" disabled>
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Esporta
            </Button>
          </div>
        </div>

        {controlloLoading && !righe.length ? (
          <div className="py-16 text-center text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            Caricamento controllo mensile…
          </div>
        ) : righeFiltrate.length === 0 ? (
          <p className="text-sm text-slate-500 py-12 text-center">Nessun ospite da fatturare per il periodo selezionato.</p>
        ) : (
          <>
          {(selectableRighe.length > 0 || selectedFatturaIds.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/80">
              <Checkbox
                checked={allSelectableSelected ? true : someSelectableSelected ? "indeterminate" : false}
                onCheckedChange={(v) => toggleSelectAll(v === true)}
                disabled={!selectableRighe.length || !!actionLoading}
                aria-label="Seleziona tutte le bozze"
              />
              <button
                type="button"
                className="text-sm font-medium text-slate-700 hover:text-slate-900 disabled:opacity-50"
                disabled={!selectableRighe.length || !!actionLoading}
                onClick={() => toggleSelectAll(!allSelectableSelected)}
              >
                {allSelectableSelected ? "Deseleziona tutte" : "Seleziona tutte"}
                {selectableRighe.length > 0 && (
                  <span className="text-slate-500 font-normal">
                    {" "}({selectableRighe.length} bozze
                    {filtroStato && filtroStato !== "__tutti__" ? ` · filtro attivo` : ""})
                  </span>
                )}
              </button>
              {selectedFatturaIds.length > 0 && (
                <span className="text-sm text-slate-500">
                  {selectedFatturaIds.length} selezionate
                </span>
              )}
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10">
                  {selectableRighe.length > 0 ? (
                    <Checkbox
                      checked={allSelectableSelected ? true : someSelectableSelected ? "indeterminate" : false}
                      onCheckedChange={(v) => toggleSelectAll(v === true)}
                      disabled={!!actionLoading}
                      aria-label="Seleziona tutte le bozze"
                    />
                  ) : null}
                </TableHead>
                <TableHead className="text-slate-500 font-medium">Ospite</TableHead>
                <TableHead className="text-slate-500 font-medium">Camera</TableHead>
                <TableHead className="text-slate-500 font-medium">Periodo di fatturazione</TableHead>
                <TableHead className="text-slate-500 font-medium text-right">Retta</TableHead>
                <TableHead className="text-slate-500 font-medium text-right">Extra</TableHead>
                <TableHead className="text-slate-500 font-medium text-right">Totale</TableHead>
                <TableHead className="text-slate-500 font-medium">Origine importi</TableHead>
                <TableHead className="text-slate-500 font-medium">Stato fattura</TableHead>
                {isFic && (
                  <TableHead className="text-slate-500 font-medium text-center">Su FIC</TableHead>
                )}
                <TableHead className="text-slate-500 font-medium">Stato pagamento</TableHead>
                <TableHead className="text-slate-500 font-medium text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {righeFiltrate.map((row) => {
                const selKey = row.fattura_id || `f-${row.fascicolo_id}`;
                const checked = selectedIds.has(selKey);
                return (
                  <TableRow key={row.fascicolo_id}>
                    <TableCell>
                      {isRigaSelezionabile(row) && (
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => toggleSelect(row.fattura_id, row.fascicolo_id, !!v)}
                          disabled={!!actionLoading}
                          aria-label={`Seleziona ${row.ospite || row.fattura_id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-0">
                        <AvatarIniziali iniziali={row.iniziali} />
                        <span className="font-medium text-slate-800 truncate">{row.ospite}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{row.camera}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[140px]">{row.periodo}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatEuro(row.retta)}</TableCell>
                    <TableCell className="text-right">
                      <ExtraCell extra={row.extra} dettaglio={row.extra_dettaglio} formatEuro={formatEuro} />
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatEuro(row.totale)}
                    </TableCell>
                    <TableCell>
                      <OrigineImportiBadge origine={row.origine_importi || "automatica"} />
                    </TableCell>
                    <TableCell>
                      <StatoBadge stato={row.stato_fattura} map={STATO_FATTURA_CONTROLLO} />
                    </TableCell>
                    {isFic && (
                      <TableCell className="text-center">
                        <FicPresenzaCell
                          presenza={ficPresenzaByFascicolo[row.fascicolo_id]}
                          loading={ficPresenzaLoading}
                          formatEuro={formatEuro}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <StatoBadge stato={row.stato_pagamento} map={STATO_PAGAMENTO_CONTROLLO} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {row.stato_fattura === "mancante" && (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50"
                              asChild
                            >
                              <Link
                                to={`/fatturazione/nuova?fascicoloId=${row.fascicolo_id}&mese=${meseControllo}`}
                              >
                                <Plus className="w-3.5 h-3.5 mr-1" />
                                Crea fattura
                              </Link>
                            </Button>
                            {isFic && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs border-sky-200 text-sky-800 hover:bg-sky-50"
                                onClick={() => {
                                  setFicDialogFascicoloId(row.fascicolo_id);
                                  setFicDialogOpen(true);
                                }}
                              >
                                <Link2 className="w-3.5 h-3.5 mr-1" />
                                FIC
                              </Button>
                            )}
                          </>
                        )}
                        {row.origine_documento === "fic_collegata" && (
                          <Badge className="text-[10px] bg-sky-100 text-sky-800">FIC</Badge>
                        )}
                        {row.fattura_id && (
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                            title="Dettaglio"
                            onClick={() => openDetail(row.fattura_id)}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {isFic && row.fattura_id && row.stato_fattura === "approvata" && openRecuperoFic && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs border-violet-200 text-violet-800 hover:bg-violet-50"
                            title="Cerca su FIC e collega documento esistente"
                            onClick={() => openRecuperoFic(row.fattura_id)}
                          >
                            <Link2 className="w-3.5 h-3.5 mr-1" />
                            Recupera FIC
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </>
        )}
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600 px-1">
        <div className="flex flex-wrap gap-4">
          <span className="font-medium text-slate-700">Origine importi:</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            Automatica — calcolata dal sistema
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            Modificata — importi aggiornati manualmente
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            Manuale — inserita senza collegamento automatico
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-base">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <ClipboardList className="w-4 h-4 text-slate-500" />
              Dettaglio extra del mese
            </h3>
            <ul className="space-y-2 text-sm">
              {(controlloData?.extra_aggregati || [])
                .filter((x) => x.categoria !== "_totale")
                .map((x) => (
                  <li key={x.categoria} className="flex justify-between">
                    <span className="text-slate-600">{x.label}</span>
                    <span className="font-medium tabular-nums">{formatEuro(x.importo)}</span>
                  </li>
                ))}
              {(controlloData?.extra_aggregati || []).find((x) => x.categoria === "_totale") && (
                <li className="flex justify-between border-t pt-2 font-semibold">
                  <span>Totale extra</span>
                  <span>
                    {formatEuro(
                      (controlloData.extra_aggregati || []).find((x) => x.categoria === "_totale")?.importo,
                    )}
                  </span>
                </li>
              )}
              {!(controlloData?.extra_aggregati || []).length && (
                <li className="text-slate-400 text-xs">Nessun extra nel mese</li>
              )}
            </ul>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <UserPlus className="w-4 h-4 text-emerald-600" />
              Nuovi ingressi nel mese ({(controlloData?.ingressi || []).length})
            </h3>
            <ul className="space-y-3 text-sm">
              {(controlloData?.ingressi || []).map((i) => (
                <li key={i.fascicolo_id}>
                  <p className="font-medium text-slate-800">{i.nome}</p>
                  <p className="text-xs text-slate-500">
                    Ingresso {new Date(i.data_ingresso).toLocaleDateString("it-IT")}
                  </p>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    Fatturazione prevista: {i.giorni_fatturazione} giorni
                  </p>
                </li>
              ))}
              {!(controlloData?.ingressi || []).length && (
                <li className="text-slate-400 text-xs">Nessun ingresso nel mese</li>
              )}
            </ul>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <UserMinus className="w-4 h-4 text-amber-600" />
              Dimessi nel mese ({(controlloData?.dimessi || []).length})
            </h3>
            <ul className="space-y-3 text-sm">
              {(controlloData?.dimessi || []).map((d) => (
                <li key={d.fascicolo_id}>
                  <p className="font-medium text-slate-800">{d.nome}</p>
                  <p className="text-xs text-slate-500">
                    Dimissione {new Date(d.data_dimissione).toLocaleDateString("it-IT")}
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Fatturazione prevista: {d.giorni_fatturazione} giorni
                  </p>
                </li>
              ))}
              {!(controlloData?.dimessi || []).length && (
                <li className="text-slate-400 text-xs">Nessuna dimissione nel mese</li>
              )}
            </ul>
          </CardContent>
        </Card>

        {(controlloData?.anomalie?.ospiti_non_fatturati ?? 0) > 0 ? (
          <Card className="card-base border-red-200 bg-red-50/50">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-red-800 flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" />
                Attenzione
              </h3>
              <p className="text-sm text-red-900/90">{controlloData.anomalie.messaggio}</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-3 border-red-300 text-red-800 hover:bg-red-100"
                onClick={() => {
                  setSoloMancanti(true);
                  setFiltroStato("mancante");
                }}
              >
                Visualizza mancanti
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-base">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Anomalie amministrative
              </h3>
              <p className="text-sm text-slate-500">Nessuna anomalia rilevata per questo mese.</p>
            </CardContent>
          </Card>
        )}
      </div>

      <ImportClientiFicDialog
        open={importClientiOpen}
        onOpenChange={setImportClientiOpen}
        headers={headers}
        strutturaIdForConfig={strutturaIdForConfig}
        onSuccess={loadControllo}
      />

      <CollegaFicDialog
        open={ficDialogOpen}
        onOpenChange={setFicDialogOpen}
        headers={headers}
        meseControllo={meseControllo}
        strutturaIdForConfig={strutturaIdForConfig}
        fascicoli={fascicoli}
        initialFascicoloId={ficDialogFascicoloId}
        onSuccess={() => {
          loadControllo();
          loadFicPresenza();
        }}
      />

      <AlertDialog
        open={!!inviaApprovateConfirm}
        onOpenChange={(open) => !open && setInviaApprovateConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Invio approvate a SDI in background</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p>
                  Stai per accodare{" "}
                  <strong>{inviaApprovateConfirm?.count ?? 0}</strong>{" "}
                  fatture per l&apos;invio tramite Fatture in Cloud.
                </p>
                <p>
                  L&apos;invio avverrà <strong>in background</strong>: le fatture verranno trasmesse al SDI
                  una alla volta, con una pausa tra un invio e l&apos;altro per rispettare i limiti del servizio.
                </p>
                <p>
                  Nella tabella comparirà lo stato <strong>In coda SDI</strong> finché ogni fattura non sarà
                  inviata. Non potrai reinviare le fatture già in coda. La lista si aggiornerà automaticamente.
                </p>
                <p>Puoi continuare a usare Affidya mentre l&apos;invio procede.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={(e) => {
                e.preventDefault();
                confirmInviaApprovate();
              }}
            >
              Avvia invio in background
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
